import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/auth";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { filter, take } from "rxjs/operators";
import { BusinessInformationEntity } from "src/app/models/business.model";
import { ServiceComboEntity } from "src/app/models/service-combo.model";
import { PostEntity } from "src/app/models/post.model";
import { BusinessService } from "src/app/services/business/business.service";
import { BusinessServiceService } from "src/app/services/business-service/business-service.service";
import { FeedbackEntity } from "src/app/models/feedback.model";
import { FeedbackService } from "src/app/services/feedback/feedback.service";
import { PostService } from "src/app/services/posts/post.service";
import * as CartActions from "src/app/store/cart/cart.actions";
import { CartItem } from "src/app/store/cart/cart.models";
import { AppState } from "src/app/store";

@Component({
  selector: "app-blogdetails",
  templateUrl: "./blogdetails.component.html",
  styleUrls: ["./blogdetails.component.css"],
})
export class BlogdetailsComponent implements OnInit {
  @ViewChild("revealContainer") revealContainer?: ElementRef<HTMLDivElement>;

  post: PostEntity | null = null;
  relatedPosts: PostEntity[] = [];
  storeInfo: BusinessInformationEntity | null = null;
  storeServices: ServiceComboEntity[] = [];
  feedbacks: FeedbackEntity[] = [];
  isLoading = false;
  isLoadingFeedback = false;
  error: string | null = null;
  sliderPercent = 50;
  isDragging = false;

  get beforeUrl(): string | undefined {
    return this.post?.beforePhotoUrl || this.post?.afterPhotoUrl;
  }

  get afterUrl(): string | undefined {
    return this.post?.afterPhotoUrl || this.post?.beforePhotoUrl;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly postService: PostService,
    private readonly businessService: BusinessService,
    private readonly businessServiceService: BusinessServiceService,
    private readonly feedbackService: FeedbackService,
    private readonly afAuth: AngularFireAuth,
    private readonly store: Store<AppState>
  ) {}

  ngOnInit(): void {
    const postFromState = history.state?.post as PostEntity | undefined;
    const postId =
      this.route.snapshot.paramMap.get("postId") ||
      this.route.snapshot.queryParamMap.get("postId");

    if (postFromState) {
      this.post = postFromState;
      this.loadSideData(postFromState);
    }

    if (postId) {
      // Always attempt to load the post; use token if present
      this.afAuth.idToken.pipe(take(1)).subscribe({
        next: () => this.loadPost(postId),
        error: () => this.loadPost(postId),
        complete: () => this.loadPost(postId),
      });
    }
  }

  private loadPost(postId: string): void {
    this.isLoading = true;
    this.postService.getPostById(postId).subscribe({
      next: (post) => {
        this.post = post;
        this.error = null;
        this.loadSideData(post);
      },
      error: (err) => {
        console.error("Failed to load post", err);
        this.error = "Unable to load this post right now.";
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  private loadSideData(post: PostEntity): void {
    if (post.storeId) {
      this.loadStoreInfo(post.storeId);
      this.loadStoreServices(post.storeId);
      this.loadStoreFeedback(post.storeId);
      this.loadRelatedPosts(post.storeId);
    } else {
      this.loadRelatedPosts();
    }
  }

  private loadRelatedPosts(storeId?: string): void {
    this.postService.getPosts({ pageSize: 6, storeId }).subscribe({
      next: (res) => {
        const posts = res?.posts || [];
        this.relatedPosts = posts.filter((p) => !this.post || p.id !== this.post.id);
      },
      error: (err) => console.error("Failed to load related posts", err),
    });
  }

  private loadStoreInfo(storeId: string): void {
    this.businessService.findById(storeId).subscribe({
      next: (store) => (this.storeInfo = store),
      error: (err) => console.error("Failed to load store info", err),
    });
  }

  private loadStoreServices(storeId: string): void {
    this.businessServiceService.getCombosForStore(storeId).subscribe({
      next: (services) => (this.storeServices = services || []),
      error: (err) => console.error("Failed to load services for store", err),
    });
  }

  private loadStoreFeedback(storeId: string): void {
    this.isLoadingFeedback = true;
    this.feedbackService.getStoreFeedback(storeId).subscribe({
      next: (feedbacks) => {
        this.feedbacks = (feedbacks || []).slice(0, 5);
        this.isLoadingFeedback = false;
      },
      error: (err) => {
        console.error("Failed to load store feedback", err);
        this.isLoadingFeedback = false;
      },
    });
  }

  getFeaturedServices(): ServiceComboEntity[] {
    return (this.storeServices || [])
      .filter((s) => s.isFeature && s.status === "active")
      .slice(0, 5);
  }

  formatDuration(duration: number): string {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (hours && minutes) return `${hours}h ${minutes}m`;
    if (hours) return `${hours}h`;
    return `${minutes}m`;
  }

  formatPrice(price: number): string {
    return `$${price.toFixed(2)}`;
  }

  formatServiceItems(service: ServiceComboEntity): string {
    if (!service.items || service.items.length === 0) {
      return "";
    }
    return service.items.map((item) => item.name).join(", ");
  }

  addServiceToCart(service: ServiceComboEntity): void {
    if (!this.storeInfo) return;
    const cartItem: CartItem = {
      id: `${this.storeInfo.id}-${service.id}-${Date.now()}`,
      storeId: this.storeInfo.id,
      storeName: this.storeInfo.storeName,
      storeAddress: `${this.storeInfo.address}${this.storeInfo.city ? ", " + this.storeInfo.city : ""}`,
      date: new Date(),
      startTime: new Date(),
      duration: service.duration,
      combos: [service],
      totalPrice: service.totalPrice,
    };
    this.store.dispatch(CartActions.addCartItem({ item: cartItem }));
  }

  onSliderChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.sliderPercent = this.clampPercent(value);
  }

  startDrag(event: MouseEvent | TouchEvent): void {
    this.isDragging = true;
    this.updateSliderFromEvent(event);
  }

  onDrag(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;
    this.updateSliderFromEvent(event);
  }

  endDrag(): void {
    this.isDragging = false;
  }

  getBeforeClipPath(): string {
    return `polygon(0 0, ${this.sliderPercent}% 0, ${this.sliderPercent}% 100%, 0 100%)`;
  }

  getAfterClipPath(): string {
    return `polygon(${this.sliderPercent}% 0, 100% 0, 100% 100%, ${this.sliderPercent}% 100%)`;
  }

  getDividerPosition(): string {
    return `${this.sliderPercent}%`;
  }

  shouldShowBeforeLabel(): boolean {
    return this.sliderPercent > 2;
  }

  shouldShowAfterLabel(): boolean {
    return this.sliderPercent < 98;
  }

  private updateSliderFromEvent(event: MouseEvent | TouchEvent): void {
    const container = this.revealContainer?.nativeElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clientX = this.getClientX(event);
    const percent = ((clientX - rect.left) / rect.width) * 100;
    this.sliderPercent = this.clampPercent(percent);
  }

  private clampPercent(value: number): number {
    if (isNaN(value)) return 50;
    return Math.min(100, Math.max(0, Math.round(value)));
  }

  private getClientX(event: MouseEvent | TouchEvent): number {
    const touchEvent = event as TouchEvent;
    if (touchEvent.touches && touchEvent.touches.length) {
      return touchEvent.touches[0].clientX;
    }
    return (event as MouseEvent).clientX;
  }
}





